import { KNOWN_VARIABLE_NAMES } from './campaign-merge';

export interface ContactLike {
  id?: string;
  first_name?: string | null;
  last_name?: string | null;
  company_name?: string | null;
  job_title?: string | null;
  primary_email?: string | null;
  primary_phone?: string | null;
}

export interface DiagnosticsResult {
  rendered: string;
  filled: string[];
  missing: string[];
  unknown: string[];
}

// Re-declared mirror of campaign-merge.ts `variableMap` (that map is NOT
// exported — it lives inside renderCampaignTemplate). The parity test in
// oneoff-merge.test.ts locks this to renderCampaignTemplate for present values.
function buildValueMap(c: ContactLike): Record<string, string> {
  return {
    first_name: c.first_name || '',
    firstname: c.first_name || '',
    last_name: c.last_name || '',
    lastname: c.last_name || '',
    company: c.company_name || '',
    companyname: c.company_name || '',
    email: c.primary_email || '',
    occupation: c.job_title || '',
    phone: c.primary_phone || '',
  };
}

// Map alias forms to their canonical names for diagnostics reporting
const ALIAS_TO_CANONICAL: Record<string, string> = {
  firstname: 'first_name',
  lastname: 'last_name',
  companyname: 'company',
};

// Whitespace-tolerant, word-char token names. Mirrors detectVariableIssues'
// doubleRe in template-utils.ts.
const TOKEN_RE = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;

export function renderTemplateWithDiagnostics(
  template: string,
  contact: ContactLike,
): DiagnosticsResult {
  const values = buildValueMap(contact);
  const filled = new Set<string>();
  const missing = new Set<string>();
  const unknown = new Set<string>();

  const rendered = template.replace(TOKEN_RE, (match, rawName: string) => {
    const name = rawName.toLowerCase();
    if (!KNOWN_VARIABLE_NAMES.has(name)) {
      unknown.add(rawName);
      return match; // leave literal
    }
    const canonical = ALIAS_TO_CANONICAL[name] ?? name;
    const value = values[name] ?? '';
    if (value.trim().length > 0) {
      filled.add(canonical);
      return value;
    }
    missing.add(canonical); // present in template but contact has no value
    return match; // leave literal so the user sees the gap
  });

  return {
    rendered,
    filled: [...filled],
    missing: [...missing],
    unknown: [...unknown],
  };
}

function stripHtml(input: string): string {
  if (typeof document !== 'undefined') {
    const el = document.createElement('div');
    el.innerHTML = input;
    return el.textContent || '';
  }
  return input.replace(/<[^>]*>/g, '');
}

// Deduped full `{{}}` matches found in the plain-text projection of `input`.
// NOTE: a token split across HTML tags (e.g. <strong>{{</strong>x}}) is not
// detected — documented limitation.
export function findMergeTokens(input: string): string[] {
  const text = stripHtml(input);
  const found = new Set<string>();
  for (const m of text.matchAll(/\{\{\s*[^{}]*?\s*\}\}/g)) {
    found.add(m[0]);
  }
  return [...found];
}

// Deduped full `{{}}` matches found in the RAW HTML string WITHOUT stripping
// tags. Unlike findMergeTokens, this sees tokens that live inside attributes
// (e.g. `<a href="{{x}}">`) — stripHtml/textContent would drop those because
// attribute text is not part of textContent.
export function findMergeTokensInHtml(html: string): string[] {
  const found = new Set<string>();
  for (const m of html.matchAll(/\{\{\s*[^{}]*?\s*\}\}/g)) {
    found.add(m[0]);
  }
  return [...found];
}

export function humanizeToken(token: string): string {
  const cleaned = token
    .replace(/[{}]/g, '')
    .trim()
    .toLowerCase()
    .replace(/_/g, ' ');
  // Never return an empty/garbled label (e.g. `{{ }}`, `{{123}}`): if there
  // are no letters to humanize, fall back to the raw token text so the warning
  // still points at something the user can find.
  if (!/[a-z]/.test(cleaned)) return token;
  return cleaned;
}
